import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /dtv — TH hub for Destination Thailand Visa (5-year multiple entry)
// Phase P-DTV-DIGITAL-NOMAD
import { createFileRoute, Link } from "@tanstack/react-router";
import { DTV_CATEGORIES } from "@/data/dtv-categories";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Globe2, Plane, Wallet, CalendarClock, FileText } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/dtv/")({
  loader: async () => {
    const { DTV_CATEGORIES: list } = await import("@/data/dtv-categories");
    return { index: list.map((c) => ({ slug: c.slug, name: c.titleTh })) };
  },
  head: ({ loaderData }) => {
    const index = loaderData?.index ?? [];

    const url = `${SITE}/dtv`;
    const title = `DTV — Destination Thailand Visa 5 ปี | คู่มือ Digital Nomad & Long Stay 2026 | NYC`;
    const desc = `Destination Thailand Visa 5 ปี multiple entry — Workcation, Freelancer, Muay Thai, Thai Cooking, Medical, Sports, Soft Power, Dependent. ครบทุกประเภท ขั้นตอน เอกสาร — คุยกับผู้เชี่ยวชาญตัวจริงได้เลยวันนี้`;
    return {
      meta: [
        { title: ctaTitle(title, "/dtv", "th") }, { name: "description", content: ctaDesc(desc, "/dtv", "th")},
        { property: "og:title", content: ctaTitle(title, "/dtv", "th")}, { property: "og:description", content: ctaDesc(desc, "/dtv", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "DTV, Destination Thailand Visa, digital nomad Thailand, workcation visa, Muay Thai visa, Thai cooking visa, 5-year Thai visa" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/dtv", "/en/dtv")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "th-TH",
              mainEntity: {
                "@type": "ItemList", numberOfItems: index.length,
                itemListElement: index.map((c, i) => ({
                  "@type": "ListItem", position: i + 1, name: c.name,
                  url: `${SITE}/dtv/${c.slug}`,
                })),

              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Destination Thailand Visa (DTV) Application Service",
              serviceType: "Destination Thailand Visa (DTV) Application Service",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "9500", priceCurrency: "THB", description: "DTV application + document prep + financial structure" },
            }),
          },
          faqPageSchema([
            { q: "DTV คืออะไร?", a: "Destination Thailand Visa — วีซ่า 5 ปี multiple entry ออกตั้งแต่ 15 ก.ค. 2567 พำนัก 180 วัน/ครั้ง ต่อได้ 180 วัน รวม 360 วัน/ปี ค่าธรรมเนียม 10,000 บาท" },
            { q: "เงินขั้นต่ำเท่าไหร่?", a: "หลักฐานการเงิน ≥ 500,000 บาท (หรือเทียบเท่าในสกุลต่างประเทศ) ครอบคลุมระยะ 6 เดือนล่าสุด — ใช้ Bank Statement, Investment Portfolio, Fixed Deposit หรือเอกสารแสดงรายได้ประจำ (Payslip ≥ 80,000 บาท/เดือน) ก็ได้ ต้องมีลายเซ็นและตราประทับจากธนาคารต้นทาง พร้อมแปลเป็นภาษาอังกฤษถ้าไม่ใช่ภาษาอังกฤษ" },
            { q: "DTV ทำงานในไทยได้ไหม?", a: "ห้ามทำงานกับบริษัทไทยโดยเด็ดขาด — DTV (Destination Thailand Visa) อนุญาตเฉพาะ Remote Work ให้นายจ้าง/ลูกค้าต่างประเทศ, Workation, Digital Nomad, Muay Thai Training, Medical Treatment หรือ Soft Power Activities เท่านั้น หากต้องการทำงานในไทยต้องเปลี่ยนเป็น Non-Immigrant B + Work Permit (ขั้นต่ำทุนจดทะเบียน 2 ล้านบาท)" },
            { q: "อยู่นานเกิน 180 วันต้องทำอะไร?", a: "DTV อยู่ได้ 180 วันต่อครั้ง ต่ออายุได้อีก 180 วัน (รวม 360 วันต่อรอบ) ที่สำนักงานตรวจคนเข้าเมือง ค่าธรรมเนียม 1,900 บาท · ต้องยื่น TM47 (90-Day Report) ทุก 90 วัน · DTV มีอายุ 5 ปี เข้า-ออกได้ไม่จำกัด · ถ้าอยู่ ≥ 180 วัน/ปี ภาษีเป็น Tax Resident ของไทย" },
            { q: "พาครอบครัวมาด้วยได้ไหม?", a: "ได้ — DTV Dependent สำหรับคู่สมรสที่จดทะเบียนถูกต้อง และบุตรที่ยังไม่บรรลุนิติภาวะ (อายุ ≤ 20 ปี) ค่าธรรมเนียมคนละ 10,000 บาท ใช้เอกสารจดทะเบียนสมรส/สูติบัตรที่ผ่าน Apostille หรือสถานทูตปลายทาง พร้อมแปลภาษาอังกฤษโดย Sworn Translator — NYC จัดเตรียมเอกสารครบให้" },
          ]),
        ]),
      ],
    };
  },
  component: DtvHubTh,
});

function DtvHubTh() {
  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="dtv" locale="th" />
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link><span className="mx-2">/</span><span>DTV</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Globe2 className="mr-1 inline h-3 w-3" /> Destination Thailand Visa · 9 categories
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">DTV — Destination Thailand Visa 5 ปี</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            วีซ่า 5 ปี multiple entry สำหรับ Digital Nomad, ฟรีแลนซ์, นักเรียนมวยไทย/อาหารไทย, รักษาพยาบาล, กีฬา, soft power และครอบครัว — พำนัก 180 วัน/ครั้ง ต่อได้ครบ 360 วัน/ปี
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">DTV คืออะไร และใครยื่นได้?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Destination Thailand Visa เปิดตัว 15 ก.ค. 2567 — วีซ่า 5 ปี เข้า-ออกไทยไม่จำกัด ค่าธรรมเนียม 10,000 บาท ต้องมีเงิน 500,000 บาท + sponsor 1 ใน 9 ประเภท (Workcation, Freelancer, Muay Thai, Thai Cooking, Medical, Sports, Seminar, Soft Power, Dependent) ใช้ remote work ได้ แต่ห้ามรับเงินจากนายจ้างในไทย
            </p>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-4 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><CalendarClock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">อายุวีซ่า</div><div className="text-sm font-bold">5 ปี</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Plane className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">เข้าออก</div><div className="text-sm font-bold">ไม่จำกัด</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">ค่าธรรมเนียม</div><div className="text-sm font-bold">10,000 บาท</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><FileText className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">พำนัก/ครั้ง</div><div className="text-sm font-bold">180+180 วัน</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/visa" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <FileText className="h-4 w-4" /> วีซ่าทุกประเภท
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">9 ประเภทของ DTV</h2>
          <p className="mt-2 text-sm text-muted-foreground">เลือกประเภทที่ตรงกับ sponsor ของคุณ · คลิกเพื่อดูเอกสาร ขั้นตอน และค่าใช้จ่าย</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DTV_CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to="/dtv/$category"
                params={{ category: c.slug }}
                className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                data-track-zone="dtv-hub-th"
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{c.icon}</span>
                  <div className="font-bold text-sm">{c.titleTh}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{c.taglineTh}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">฿{c.servicePriceTHB.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    ดู <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold">บริการที่เกี่ยวข้อง</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Visa Matrix — เปรียบเทียบ 7 วีซ่า", d: "DTV vs LTR vs Non-O ใน 60 วินาที", to: "/visa-matrix" as const },
              { t: "Visa Compliance", d: "TM30 / 90-day report / Re-entry", to: "/visa-compliance" as const },
              { t: "วีซ่าทุกประเภท", d: "Non-B / LTR / Retirement / Marriage", to: "/visa" as const },
              { t: "แลกใบขับขี่", d: "ใช้ Certificate of Residence", to: "/driving-license" as const },
              { t: "แปลเอกสารรับรอง", d: "ทะเบียนสมรส, employer letter", to: "/translate" as const },
              { t: "Apostille", d: "สำหรับเอกสารต่างประเทศ", to: "/apostille" as const },
              { t: "Work Permit", d: "ถ้าจะทำงานกับบริษัทไทย", to: "/work-permit" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="th" />
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
